/**
 * Pure helpers that turn a `POST /v1/chat` answer's `chart` and `query` payloads
 * into the shapes the panel renders — the inline horizontal-bar rows and a
 * truthful chart title. No fabricated values: every number comes from the API
 * response.
 *
 * `dashboardTarget` and `queryText` translate the `query` payload for the
 * "open in dashboard" jump and the pipeline disclosure. **Neither is rendered
 * today** — the chat panel deliberately shows no query chrome (see
 * `BotAnswer`) — but the endpoint still returns `query` and `filters`, the
 * gateway still audits every aggregation, and these remain the tested
 * translation of that contract. They are kept on purpose, not left behind:
 * restoring either affordance is a render change, not a rewrite.
 */
import type { HBarDatum } from '../../charts';
import type { ChartResponse, QueryResponse } from '../../api/schema';

/**
 * The backend returns dashboard filters in the analytics API vocabulary
 * (`location_id` / `scanner_id`); the Dashboard URL uses the shared, human
 * names (`location` / `scanner`). Everything else passes through unchanged.
 */
const FILTER_KEY_MAP: Record<string, string> = {
  location_id: 'location',
  scanner_id: 'scanner',
};

/**
 * A chart is a time series (line) or a categorical breakdown (bars).
 *
 * A ranked breakdown shows only its leaders, so the title has to say so — "top 3
 * of 21" — or three bars would read as the entire distribution. `total_points` is
 * only set when rows were actually left out.
 */
export function chartTitle(chart: ChartResponse): string {
  if (chart.type === 'line') return 'Documents over time';
  const total = chart.total_points;
  return total != null && total > chart.data.length
    ? `Breakdown — top ${String(chart.data.length)} of ${String(total)}`
    : 'Breakdown';
}

/** Map the answer's chart points to the horizontal-bar primitive's rows. */
export function chartToBars(chart: ChartResponse): HBarDatum[] {
  return chart.data.map((point) => ({ label: point.label, value: point.value }));
}

/** A screen-reader summary of the inline chart, built from the real points. */
export function chartSummary(chart: ChartResponse): string {
  const parts = chart.data.map((point) => `${point.label} ${String(point.value)}`);
  return `${chartTitle(chart)}: ${parts.join(', ')}`;
}

export interface DashboardTarget {
  /** Router path (with query string) to navigate to. */
  href: string;
  /** True when the answer is about automation opportunities, not the dashboard. */
  automation: boolean;
}

/**
 * The "open in dashboard" destination derived from the executed query. An
 * automation-opportunity answer routes to the Automation screen; everything else
 * opens the Dashboard pre-filtered by the answer's filter payload.
 */
export function dashboardTarget(query: QueryResponse): DashboardTarget {
  if (query.collection.toLowerCase().includes('automation')) {
    return { href: '/automation', automation: true };
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query.filters)) {
    if (value === '') continue;
    params.set(FILTER_KEY_MAP[key] ?? key, value);
  }
  const qs = params.toString();
  return { href: qs === '' ? '/dashboard' : `/dashboard?${qs}`, automation: false };
}

/** The aggregation the gateway ran, rendered as a Mongo shell call for the disclosure. */
export function queryText(query: QueryResponse): string {
  const body = JSON.stringify(query.pipeline, null, 2);
  return `db.${query.collection}.aggregate(\n${body}\n)`;
}
